import { ActionIcon, Avatar, Button, Flex, Loader, Modal, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCapture, IconCheck, IconPlus, IconX } from "@tabler/icons-react";
import { memo, useRef, useState } from "react";

type Props = {
  onCapture: (file: File) => void;
  uploadingImage: boolean;
  showTitle?: boolean;
};

export const CameraAction: React.FC<Props> = memo(
  ({ onCapture, uploadingImage, showTitle }) => {
    const [opened, { open, close }] = useDisclosure(false);
    const [captured, setCaptured] = useState(false);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const [jobImage, setJobImage] = useState<string>('');

    const startCamera = () => {
      open();
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const constraints = {
          video: {
            facingMode: "environment", // This will prefer the back camera on mobile devices
          },
        };
        navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
          let video = videoRef.current;
          if (video) {
            video.srcObject = stream;
            video.play();
          }
        }).catch(error => {
          console.error("Error accessing camera:", error);
          close();
        });
      }
    };

    const captureImage = () => {
      if (canvasRef.current && videoRef.current) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        const context = canvas.getContext("2d");

        if (context) {
          // Set canvas dimensions to match video dimensions for proper aspect ratio
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          setCaptured(true);
        }
      }
    };

    const onStopVideo = () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        const tracks = stream.getTracks();

        tracks.forEach(function (track) {
          track.stop();
        });

        videoRef.current.srcObject = null;
      }
    };

    const convertCanvasIntoFile = () => {
      if (canvasRef.current) {
        canvasRef.current.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], "photo.jpg", {
                type: "image/jpeg",
                lastModified: Date.now(),
              });

              close();
              onStopVideo();
              onCapture(file);

              // Create a URL for the image preview
              const url = URL.createObjectURL(blob);
              setJobImage(url);
              setCaptured(false);
            }
          },
          "image/jpeg",
          0.9
        );
      }
    };

    const handleRetake = () => {
      setCaptured(false);
      setJobImage('');
      close();
      startCamera()
    };

    return (
      <>
        {showTitle && <Text>{`Upload Invoice Image`}</Text>}
        <Avatar
          size={"xl"}
          onClick={jobImage ? open : startCamera}
          src={jobImage}
        >
          {uploadingImage ? (
            <Loader color="blue" />
          ) : (
            <IconPlus size={20} style={{ marginRight: "5px" }} />
          )}
        </Avatar>

        {jobImage ? (
          <Modal
            opened={opened}
            withCloseButton={false}
            onClose={close}
            size="md"
            radius="md"
            centered
            styles={{
              content: {
                maxHeight: '80vh',
              },
              overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              },
            }}
            transitionProps={{ transition: "fade", duration: 200 }}
          >
            <Flex direction="column" align="center" gap="md">
              <div style={{ maxHeight: '60vh', overflow: 'auto', width: '100%' }}>
                <img src={jobImage} alt="job image" style={{ width: '100%', objectFit: 'contain' }} />
              </div>

              <Flex gap="md" justify="center" mt="md">
                <Button onClick={close} variant="outline" color="gray">
                  Close
                </Button>
                <Button
                  onClick={handleRetake}
                  variant="gradient"
                  gradient={{ from: "#2B2B2B", to: "#404040", deg: 180 }}
                >
                  Retake
                </Button>
              </Flex>
            </Flex>
          </Modal>
        ) : (
          <Modal
            opened={opened}
            withCloseButton={false}
            onClose={() => {
              onStopVideo();
              setCaptured(false);
              close();
            }}
            fullScreen
            radius={0}
            styles={{
              overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              },
            }}
            transitionProps={{ transition: "fade", duration: 200 }}
          >
            <div style={{ zIndex: "100" }}>
              <canvas
                ref={canvasRef}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: captured ? 1 : 0,
                  zIndex: captured ? 100 : -1,
                }}
              />

              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: captured ? 0 : 1,
                  zIndex: captured ? -1 : 100,
                }}
              />

              {!captured ? (
                <ActionIcon
                  style={{
                    position: "absolute",
                    bottom: "50px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 101,
                  }}
                  variant="filled"
                  aria-label="Capture"
                  onClick={captureImage}
                >
                  <IconCapture
                    style={{ width: "100%", height: "100%" }}
                    stroke={1.5}
                  />
                </ActionIcon>
              ) : (
                <Flex
                  gap={"lg"}
                  pos={"absolute"}
                  bottom={"50px"}
                  left={"50%"}
                  style={{ transform: "translateX(-50%)", zIndex: 101 }}
                >
                  <ActionIcon
                    variant="filled"
                    aria-label="Cancel"
                    color="red"
                    onClick={() => setCaptured(false)}
                  >
                    <IconX
                      style={{ width: "100%", height: "100%" }}
                      stroke={1.5}
                    />
                  </ActionIcon>
                  <ActionIcon
                    variant="filled"
                    aria-label="Confirm"
                    color="green"
                    onClick={convertCanvasIntoFile}
                  >
                    <IconCheck
                      style={{ width: "100%", height: "100%" }}
                      stroke={1.5}
                    />
                  </ActionIcon>
                </Flex>
              )}
            </div>
          </Modal>
        )}
      </>
    );
  }
);